// src/encounter/services/l360-order-authorized.ts
// After a physician's sign-and-lock succeeds, tell Longevity 360 which lab orders were authorised
// (contract connect-contract.md, node L1: event `lab.order_authorized`, POST /workflows/care-models/events).
//
// FIRE-AND-REPORT, like advance-care-models-on-sign.ts. The sign is the clinical act; this is a consequence.
//   · it runs only AFTER CreateSignLock succeeded (the caller is `useCreateSignLock`'s onSuccess);
//   · nothing here returns a promise, so a slow, failing or missing Workflows endpoint cannot delay,
//     fail or undo the sign;
//   · nothing is ever a toast. A failed post is logged by the helper (`postL360Event`); a refusal by the gate
//     (409) is logged here with the server's reason; a failed A&P read or a thrown error is one short
//     `console.warn`. No log carries ids or patient data. Accepted, no-enrolment and disabled log nothing;
//   · with no sign-lock id (the POST returned none) nothing is read or sent: the id is the event's source.
// ONLY a sign-and-lock sends (`sign && lock`, not a vaccination sign), and only when the fresh A&P holds
// at least one live lab order. The A&P is read fresh, because the sign record carries no orders.
//
// Default OFF behind `env.l360EventsEnabled` (VITE_L360_EVENTS) — see env.ts.
import { env } from '@/config/env';
import { get } from '@/shared/api/api-client';
import type { AssessmentPlan, PatientAssessment } from '@/types/assessment-api';
import type { SignLockModel } from '@/types/sign-lock-api';
import { classifyRadiologyRow } from '../lib/order-category';
import { postL360Event } from './l360-api';

export interface LabOrderRef {
  id: string;
  code: string;
}

/** The live lab orders of an A&P (imaging, deleted and id- or code-less rows dropped), de-duplicated by id, in order. PURE. */
export function labOrdersOf(assessment: PatientAssessment | null | undefined): LabOrderRef[] {
  const out: LabOrderRef[] = [];
  const seen = new Set<string>();
  const walk = (conditions: AssessmentPlan[] | null | undefined) => {
    for (const cond of conditions ?? []) {
      if (!cond) continue;
      const child = cond.childMedicalCode;
      for (const row of child?.radiologyOrderRequest ?? []) {
        if (!row) continue;
        const id = row.id;
        const code = row.code?.code;
        if (row?.isDeleted === true || !id || !code || seen.has(id)) continue;
        if (classifyRadiologyRow(row) !== 'lab') continue;
        seen.add(id);
        out.push({ id, code });
      }
      walk(child?.assessmentPlanCondition);
    }
  };
  walk(assessment?.assessmentPlanCondition);
  return out;
}

async function send(
  signLockId: string,
  patientId: string,
  encounterId: string,
  performer: string,
): Promise<void> {
  try {
    const list = await get<PatientAssessment[]>(
      `/api/Patients/GetAssessmentByPatientId?PatientId=${encodeURIComponent(patientId)}&EncounterId=${encodeURIComponent(encounterId)}`,
    );
    const orders = labOrdersOf(list?.[0]);
    if (orders.length === 0) return;
    // Awaited only inside this detached task: the helper never rejects, but a throw here still lands in the catch.
    const result = await postL360Event({
      eventType: 'lab.order_authorized',
      patientId,
      sourceRef: signLockId,
      revision: 1,
      payload: {
        encounterId,
        orderIds: orders.map((o) => o.id),
        memberCodes: orders.map((o) => o.code),
        signedBy: performer,
      },
    });
    // `failed` is already logged by the helper; accepted, no-enrolment and disabled are normal. Only a refusal is said here.
    if (result.kind === 'refused') console.warn('L360: lab.order_authorized was refused by the gate:', result.reason);
  } catch {
    // Dev console only. No ids, no patient data, never a toast.
    console.warn('L360: lab.order_authorized was not sent.');
  }
}

/** Called from a SUCCESSFUL CreateSignLock with the new id and the record that was signed. Returns nothing, on purpose. */
export function notifyLabOrderAuthorized(signLockId: string | null | undefined, signed: SignLockModel): void {
  if (!env.l360EventsEnabled) return;
  const { subject, encounter, performer } = signed;
  if (signed.sign !== true || signed.lock !== true || signed.isVaccinationSign === true) return;
  if (!subject || !encounter || !performer) return;
  // The sign-lock id is the idempotent source of the event. Without it nothing is read or sent.
  if (!signLockId) {
    console.warn('L360: lab.order_authorized was not sent (no sign-lock id).');
    return;
  }
  void send(signLockId, subject, encounter, performer);
}
